<script setup lang="ts">
import { ref } from 'vue'

import { formatDateInTimezone } from '@collector/shared-utils'

import FormControl from './FormControl.vue'

// props
interface Props {
  mode?: 'time' | 'datetime' | 'date'
  min?: string
  max?: string
  size?: 'sm' /** 32px */
}

withDefaults(defineProps<Props>(), {
  mode: 'date',
  size: 'sm',
})

// emits
type Emits = {
  blur: [{ event: Event; formattedValue: string }]
}

const emit = defineEmits<Emits>()

// state
const selectedDate = defineModel<string>()
const localValue = ref(selectedDate.value)
const dateInput = ref<HTMLInputElement | null>(null)

// methods
function openCalendar(): void {
  if (dateInput.value) {
    dateInput.value.showPicker()
    dateInput.value.focus()
  }
}

function onBlur(event: Event): void {
  const target = event.target as HTMLInputElement

  if (!target.value) {
    selectedDate.value = localValue.value
    return
  }

  const formattedValue = formatDateInTimezone(
    target.value,
    'yyyy-MM-dd HH:mm:ss',
  )

  emit('blur', {
    event,
    formattedValue,
  })

  localValue.value = target.value
}
</script>

<template>
  <FormControl>
    <template #label>
      <slot />
    </template>

    <div class="relative w-full">
      <input
        ref="dateInput"
        v-model="selectedDate"
        class="border-neutral-light-0 bg-neutral-light-10 h-8 w-full rounded-sm border px-1 text-xs [&::-webkit-calendar-picker-indicator]:opacity-0"
        :type="mode === 'datetime' ? 'datetime-local' : mode"
        :min
        :max
        @blur="onBlur"
      />
      <img
        class="bg-neutral-light-10 absolute top-2 right-2 h-[1rem] w-[1.1rem] cursor-pointer"
        src="../assets/calendar.svg"
        @click="openCalendar"
      />
    </div>
  </FormControl>
</template>
